import React, {useState, useMemo} from 'react';
import {Table} from "react-bootstrap";
import {CheckCircleFill, Eye, Hourglass, Trash, XCircleFill} from "react-bootstrap-icons";
import {Session} from "../../types/admin";
import {formatDateTime, formatDuration} from "./utils";
import {ADMIN_LANG} from "../../settings";
import {useNavigate} from "react-router-dom";
import {deleteSession} from "../../app/slices/adminGameSessions";
import {useAppDispatch} from "../../app/store";
import Confirm from "./Confirm";

type Props = {
    sessions: Session[];
};

const SessionTable = ({sessions}: Props) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState<string | undefined>(undefined);

    const handleDetailsOpen = (session_id: string) => {
        navigate(`/admin/sessions/${session_id}`);
    };

    const handleDelete = (sessionId: string) => {
        setSelectedSessionId(sessionId);
        setShowConfirmDelete(true);
    };

    const handleConfirm = (sessionId?: string) => {
        if (sessionId) {
            dispatch(deleteSession(sessionId));
            setSelectedSessionId(undefined);
            setShowConfirmDelete(false);
        }
    };

    const handleCancel = () => {
        setShowConfirmDelete(false);
    };

    const sessionRows = useMemo(() => sessions.map((session: Session) => {
        const duration = session.duration ? formatDuration(session.duration) : '';
        return (
            <tr key={session.key}>
                <td>{session.name}</td>
                <td>{formatDateTime(session?.date_start)}</td>
                <td>
                    {session?.date_end ? (
                        formatDateTime(session.date_end)
                    ) : (
                        session?.live ? (
                            <span className='badge bg-primary'>Live</span>
                        ) : (
                            ''
                        )
                    )}
                </td>
                <td>{duration}</td>
                <td>{session.page} / {session.pages}</td>
                <td>{session.points} / {session.max_points}</td>
                <td className='text-center'>
                    {session.date_end ? (
                        session.passed ? (
                            <CheckCircleFill className="text-success" />
                        ) : (
                            <XCircleFill className="text-danger" />
                        )
                    ) : (
                        <Hourglass className="text-secondary" />
                    )}
                </td>
                <td className='text-end'>
                    <span className='me-2 cursor-pointer'
                          onClick={() => handleDetailsOpen(session.session_id)}><Eye/></span>
                    <span className='cursor-pointer' onClick={() => handleDelete(session.session_id)}>
                        <Trash className='text-danger'/>
                    </span>
                </td>
            </tr>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [sessions]);

    return (
        <div className='table-container'>
            <Table responsive hover striped>
                <thead>
                <tr>
                    <th>{ADMIN_LANG.name}</th>
                    <th>{ADMIN_LANG.started}</th>
                    <th>{ADMIN_LANG.ended}</th>
                    <th>{ADMIN_LANG.duration}</th>
                    <th>{ADMIN_LANG.pages}</th>
                    <th>{ADMIN_LANG.points}</th>
                    <th className='text-center'>{ADMIN_LANG.passed}</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {sessionRows}
                </tbody>
            </Table>
            <Confirm
                show={showConfirmDelete}
                title={`${ADMIN_LANG.delete} ${ADMIN_LANG.session}`}
                message={ADMIN_LANG.confirm_delete_session}
                itemId={selectedSessionId}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </div>
    );
};

export default SessionTable;